import {
    ChangeEvent,
    FC,
    FormEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import {
    Button,
    Col,
    CustomInput,
    FormGroup,
    Label,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from 'reactstrap'

import { InputLabel } from '../../../@paco/components'
import { Department } from '../../../@paco/entities/Department/Department'
import { isFetchResultSuccessful } from '../../../@paco/entities/FetchResult'
import { PayrollPeriod } from '../../../@paco/entities/PayrollPeriod/PayrollPeriod'
import { doesPeriodOverlapWithLockedPayrollPeriod } from '../../../@paco/entities/PayrollPeriod/PayrollPeriodHelpers'
import { transformToPeriod } from '../../../@paco/entities/Period/PeriodTransformers'
import { Setting } from '../../../@paco/entities/Setting/Setting'
import { getAbnormalTrackDurationThresholdSettingValue } from '../../../@paco/entities/Setting/SettingHelpers'
import { TrackFormData } from '../../../@paco/entities/Track/Track'
import { checkTrackOverlapApiCall } from '../../../@paco/entities/Track/TrackService'
import { MealType } from '../../../@paco/types'
import { areIntervalsOverlapping } from '../../../@paco/helpers/date'
import ButtonHistory from '../../../components/ButtonHistory/ButtonHistory'
import FormDatePicker from '../../../components/FormDatePicker/FormDatePicker'
import TrackDurationTooLongWarning from '../../../components/TrackDurationTooLongWarning/TrackDurationTooLongWarning'
import UserPageLink from '../../../components/UserPageLink/UserPageLink'
import { BREAK_TIMES, MEALS } from '../../../constants'
import { getUserFullName } from '../../../helpers'
import {
    compareAsc,
    formatDate,
    isValid,
    setHours,
    setMinutes,
} from '../../../helpers/date'
import transformTimeStringToNumber from '../../../helpers/date/transformTimeStringToNumber'
import UpToAndIncludingDate from '../../../helpers/date/UpToAndIncludingDate'
import { translate } from '../../../helpers/translations/translator'
import { getShiftStartAndEndTimeOfTrack } from '../helpers/getShiftStartAndEndTimeOfTrack'
import getEditTrackError from './helpers/getEditTrackError'

import './EditTrack.scss'

interface EditTrackLike {
    id: string;
    accepted: boolean;
    pause?: number;
    meal?: MealType;
    totalBillableTime?: string;
    owner: {
        id: string;
        departments?: Department[];
    };
    department?: {
        id: string;
    };
    billablePeriod?: {
        start: Date;
        end?: Date;
    };
    checkInOutPeriod?: {
        start: Date;
    };
    billableStart?: Date;
    billableEnd?: UpToAndIncludingDate;
    checkIn?: Date;
    checkOut?: UpToAndIncludingDate;
    shift?: {
        period?: {
            start?: string | Date;
            end?: string | Date;
        };
        start?: string | Date;
        end?: string | Date;
        shiftPlannings?: {
            customStartTime?: boolean;
            user?: { id: string };
            period?: {
                start: string | Date;
                end: string | Date;
            };
            start?: string | Date;
            end?: string | Date;
        }[];
    } | undefined;
    endedInManagerApp?: boolean;
}

interface OverlapCheckTrack {
    id: string;
    owner: { id: string };
    billablePeriod?: { start: Date; end: Date };
    billableStart?: Date;
    billableEnd?: { date: Date } | Date;
}

type OverlapCheckTracks = OverlapCheckTrack[];

const getCheckInDate = (track: EditTrackLike): Date => {
    if (track.billablePeriod?.start && isValid(track.billablePeriod.start)) {
        return track.billablePeriod.start
    }

    if (track.billableStart && isValid(track.billableStart)) {
        return track.billableStart
    }

    if (track.checkInOutPeriod?.start && isValid(track.checkInOutPeriod.start)) {
        return track.checkInOutPeriod.start
    }

    return new Date()
}

const getCheckOutDate = (track: EditTrackLike): Date => {
    if (track.billablePeriod?.end && isValid(track.billablePeriod.end)) {
        return track.billablePeriod.end
    }

    if (track.billableEnd && isValid(track.billableEnd.date)) {
        return track.billableEnd.date
    }

    return new Date()
}

interface EditTrackProps {
    canEdit: boolean;
    settings: Setting[];
    payrollPeriods: PayrollPeriod[];
    track: EditTrackLike;
    title: string;
    otherTracksFromOwner?: OverlapCheckTracks;
    onSubmit: (
        trackId: string,
        departmentId: string,
        formData: TrackFormData,
        approved?: boolean,
        endedOpenTrackFromManager?: boolean,
    ) => void;
    onCancel: () => void;
    onShowLogsClick?: () => void;
}

const EditTrack: FC<EditTrackProps> = ({
    canEdit,
    payrollPeriods,
    settings,
    track,
    title,
    otherTracksFromOwner = [],
    onSubmit,
    onCancel,
    onShowLogsClick,
}) => {
    const formRef = useRef<HTMLFormElement>(null)

    const [checkIn, setCheckIn] = useState<Date>(() => getCheckInDate(track))
    const [checkOut, setCheckOut] = useState<Date>(() => getCheckOutDate(track))

    const [checkInDate, setCheckInDate] = useState<Date>(checkIn)
    const [checkOutDate, setCheckOutDate] = useState<Date>(checkOut)
    const [checkInTime, setCheckInTime] = useState<string>(formatDate(checkIn, 'HH:mm'))
    const [checkOutTime, setCheckOutTime] = useState<string>(formatDate(checkOut, 'HH:mm'))
    const [pause, setPause] = useState<number>(track.pause || 0)
    const [meal, setMeal] = useState<MealType>(track.meal || MealType.none)
    const [departmentId, setDepartmentId] = useState<string>(track.department?.id || '')
    const [hasDateError, setHasDateError] = useState<boolean>(false)
    const [hasPayrollPeriodError, setHasPayrollPeriodError] = useState<boolean>(false)
    const [hasLocalOverlapError, setHasLocalOverlapError] = useState<boolean>(false)
    const [hasApiOverlapError, setHasApiOverlapError] = useState<boolean>(false)
    const [showAbnormalTrackDurationWarning, setShowAbnormalTrackDurationWarning] = useState<boolean>(false)

    const hasOverlapError = hasLocalOverlapError || hasApiOverlapError

    const pauseTimeOptions = BREAK_TIMES.map(time => (
        <option key={time} value={time}>{time}</option>
    ))

    const mealOptions = MEALS.map(mealOption => (
        <option
            key={mealOption.value}
            value={mealOption.value}
        >
            {mealOption.label}
        </option>
    ))

    const checkOverlapApi = useCallback(async (
        newCheckIn: Date,
        newCheckOut: Date,
    ): Promise<void> => {
        const response = await checkTrackOverlapApiCall(
            track.owner.id,
            newCheckIn,
            newCheckOut,
            track.id,
        )

        if (isFetchResultSuccessful(response)) {
            setHasApiOverlapError(response.data.hasOverlap)
        }
    }, [track.owner.id, track.id])

    useEffect((): void => {
        const newCheckIn = setMinutes(setHours(checkInDate, Number(checkInTime.split(':')[0])), Number(checkInTime.split(':')[1]))
        const newCheckOut = setMinutes(setHours(checkOutDate, Number(checkOutTime.split(':')[0])), Number(checkOutTime.split(':')[1]))
        const dateError = compareAsc(newCheckIn, newCheckOut) === 1

        const payrollPeriodError = doesPeriodOverlapWithLockedPayrollPeriod(
            transformToPeriod(newCheckIn, newCheckOut),
            payrollPeriods,
        )

        const localOverlapError = otherTracksFromOwner.some((otherTrack) => {
            if (otherTrack.id === track.id) {
                return false
            }

            // Support both Track (billablePeriod) and TrackViewModel (billableStart/billableEnd)
            const otherStart = otherTrack.billablePeriod?.start ?? otherTrack.billableStart
            const otherEndRaw = otherTrack.billablePeriod?.end ?? otherTrack.billableEnd
            const otherEnd = otherEndRaw instanceof Date ? otherEndRaw : otherEndRaw?.date

            if (! otherStart || ! otherEnd) {
                return false
            }

            return areIntervalsOverlapping(
                { start: newCheckIn, end: newCheckOut },
                { start: otherStart, end: otherEnd },
            )
        })

        setCheckIn(newCheckIn)
        setCheckOut(newCheckOut)
        setHasDateError(dateError)
        setHasPayrollPeriodError(payrollPeriodError)
        setHasLocalOverlapError(localOverlapError)
    }, [
        checkInDate,
        checkInTime,
        checkOutDate,
        checkOutTime,
        otherTracksFromOwner,
        track.id,
    ])

    useEffect(() => {
        if (hasDateError) {
            return
        }

        const debounceTimer = setTimeout(() => {
            checkOverlapApi(checkIn, checkOut)
        }, 300)

        return () => clearTimeout(debounceTimer)
    }, [checkIn, checkOut, hasDateError, checkOverlapApi])

    useEffect(() => {
        const totalBillableTime = track?.totalBillableTime
            ? transformTimeStringToNumber(track.totalBillableTime)
            : 0

        const abnormalTrackDurationThreshold = getAbnormalTrackDurationThresholdSettingValue(settings, track?.department?.id)

        setShowAbnormalTrackDurationWarning(totalBillableTime > abnormalTrackDurationThreshold)
    }, [track, settings])

    const handleSubmit = (accepted?: boolean): void => {
        onSubmit(
            track.id,
            departmentId,
            {
                checkIn,
                checkOut,
                pause,
                meal,
                accepted,
            },
            accepted,
            track.endedInManagerApp,
        )
    }

    const handleEdit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault()

        handleSubmit()
    }

    const handleAccept = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault()

        handleSubmit(true)
    }

    const handleDepartmentChange = (e: ChangeEvent<HTMLInputElement>): void => setDepartmentId(e.currentTarget.value)
    const handlePauseChange = (e: ChangeEvent<HTMLInputElement>): void => setPause(Number(e.currentTarget.value))
    const handleMealChange = (e: ChangeEvent<HTMLInputElement>): void => setMeal(e.currentTarget.value as MealType)
    const handleCheckInDateChange = (dateString: string): void => setCheckInDate(new Date(dateString))
    const handleCheckOutDateChange = (dateString: string): void => setCheckOutDate(new Date(dateString))
    const handleCheckInTimeChange = (e: ChangeEvent<HTMLInputElement>): void => setCheckInTime(e.currentTarget.value)
    const handleCheckOutTimeChange = (e: ChangeEvent<HTMLInputElement>): void => setCheckOutTime(e.currentTarget.value)

    return (
        <form
            ref={formRef}
            onSubmit={! track.accepted ? handleAccept : handleEdit}
            className="form-edit-track"
        >
            <ModalHeader className="form-edit-track__modal-header">
                <span>{title}</span>

                {onShowLogsClick && (
                    <ButtonHistory
                        onClick={onShowLogsClick}
                        className="form-edit-track__button-history"
                    />
                )}
            </ModalHeader>

            <ModalBody>
                <Row>
                    <Col>
                        <FormGroup>
                            <Label>{translate('common.employee')}</Label>
                            <p className="form-fake-field">
                                <UserPageLink
                                    isPlain
                                    name={getUserFullName(track.owner)}
                                    id={track.owner.id}
                                />
                            </p>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <FormGroup>
                            <InputLabel
                                required
                                text={translate('common.department')}
                            />

                            <CustomInput
                                disabled={track.accepted && departmentId}
                                type="select"
                                id="department"
                                value={departmentId}
                                onChange={handleDepartmentChange}
                            >
                                {! departmentId && <option value="null" key="null">{translate('common.notSelected')}</option>}
                                {track.owner.departments?.map(departmentOption => (
                                    <option
                                        key={departmentOption.id}
                                        value={departmentOption.id}
                                    >
                                        {departmentOption.name}
                                    </option>
                                ))}
                            </CustomInput>
                        </FormGroup>
                    </Col>

                    <Col>
                        <FormGroup>
                            <Label>{translate('common.shift')}</Label>
                            {track.shift ? (
                                <p className="form-fake-field">
                                    {getShiftStartAndEndTimeOfTrack(track)}
                                </p>
                            ) : (
                                <p className="form-fake-field">
                                    {translate('pages.tracks.unplanned')}
                                </p>
                            )}
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <FormGroup>
                            <InputLabel
                                required
                                text={translate('pages.tracks.startDate')}
                            />
                            <FormDatePicker
                                selected={checkInDate}
                                invalid={hasDateError || hasPayrollPeriodError}
                                onChange={handleCheckInDateChange}
                            />
                        </FormGroup>
                    </Col>

                    <Col>
                        <FormGroup className="mb-0">
                            <InputLabel
                                required
                                text={translate('pages.tracks.startTime')}
                            />
                            <CustomInput
                                className="form-control required"
                                type="time"
                                id="start-time"
                                value={checkInTime}
                                onChange={handleCheckInTimeChange}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup>
                            <InputLabel
                                required
                                text={translate('pages.tracks.endDate')}
                            />

                            <FormDatePicker
                                selected={checkOutDate || checkInDate}
                                invalid={hasDateError || hasPayrollPeriodError}
                                feedback={getEditTrackError(hasDateError, hasPayrollPeriodError)}
                                onChange={handleCheckOutDateChange}
                            />
                        </FormGroup>
                    </Col>

                    <Col>
                        <FormGroup className="mb-0">
                            <InputLabel
                                required
                                text={translate('pages.tracks.endTime')}
                            />
                            <CustomInput
                                className="form-control required"
                                type="time"
                                id="start-time"
                                value={checkOutTime}
                                onChange={handleCheckOutTimeChange}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <FormGroup className="mb-0">
                            <InputLabel
                                required
                                text={translate('pages.tracks.pauseTime')}
                            />
                            <CustomInput
                                type="select"
                                id="pause"
                                value={pause}
                                onChange={handlePauseChange}
                            >
                                {pauseTimeOptions}
                            </CustomInput>
                        </FormGroup>
                    </Col>

                    <Col>
                        <FormGroup className="mb-0">
                            <InputLabel text={translate('common.meal')} />
                            <CustomInput
                                type="select"
                                id="meal"
                                value={meal}
                                onChange={handleMealChange}
                            >
                                {mealOptions}
                            </CustomInput>
                        </FormGroup>
                    </Col>
                </Row>

                {showAbnormalTrackDurationWarning && (
                    <Row>
                        <Col>
                            <TrackDurationTooLongWarning className="form-edit-track__warning" />
                        </Col>
                    </Row>
                )}

                {hasOverlapError && (
                    <Row className="mt-3">
                        <Col>
                            <p className="text-danger mb-0">
                                {translate('pages.tracks.trackOverlapsWithExistingTrack')}
                            </p>
                        </Col>
                    </Row>
                )}
            </ModalBody>

            <ModalFooter className="form-edit-track__modal-footer">
                <Button type="button" color="link" id="modal-close" className="form-edit-track__button-cancel" onClick={onCancel}>
                    {translate('common.cancel')}
                </Button>

                {canEdit && (
                    <Button
                        type="button"
                        color="orange"
                        disabled={! departmentId || hasDateError || hasOverlapError}
                        className="form-edit-track__button-submit"
                        onClick={handleEdit}
                    >
                        {translate('common.edit')}
                    </Button>
                )}

                {! track.accepted && (
                    <Button
                        type="submit"
                        color="green"
                        disabled={! departmentId || hasDateError || hasPayrollPeriodError || hasOverlapError}
                        className="form-edit-track__button-submit"
                        onclick={handleAccept}
                    >
                        {title}
                    </Button>
                )}
            </ModalFooter>
        </form>
    )
}

export default EditTrack
